import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { sessionStorage } from '@shared/helpers/storage'
import * as apiRequest from '@shared/api-endpoints/index.js'
import VERIFICATION_METHOD from '@us/app/config/verification-methods.js'
import groupVerificationConfig from '@us/app/config/eligibility-group-verification.json'

const eligibilitySessionHashKey = 'ELIGIBILITY_ATTEMPT_HASH'

/**
 * returns the eligibility session hash and sets it if it is not set yet
 * @param {string} corporateKey - the corporate key to encode in the hash
 * @returns {string} the eligibility session hash
 */
function getOrSetEligibilitySessionHash(corporateKey) {
  let hash = getEligibilitySessionHash()
  if (hash === null) {
    hash = btoa(`${corporateKey}${Date.now()}`)
    sessionStorage.setItem(eligibilitySessionHashKey, hash)
  }
  return hash
}

/**
 * @returns {string | null} the eligibility session hash or null if it is not set
 */
export function getEligibilitySessionHash() {
  return sessionStorage.getItem(eligibilitySessionHashKey)
}

export const useUsStore = defineStore(
  'us/app',
  () => {
    // State
    const formData = ref({
      acceptData: false,
      acceptPhoneCall: false,
      acceptTerms: false,
      acceptTracking: false,
      confirmed: false,
      dateOfBirth: '',
      email: '',
      emailCode: '',
      lastName: '',
      firstName: '',
      password: '',
      phoneNumber: '',
      voucherCode: '',
    })

    const signupContext = ref({
      webEntryPointUrl: '',
      webEntryPointReferrerUrl: '',
    })

    /** @type {import('vue').Ref<{}>} */
    const corporateEligibilityData = ref({})
    /** @type {import('vue').Ref<string>} */
    const voucher = ref(null)

    // Actions
    function updateFormData(data) {
      formData.value = { ...formData.value, ...data }
    }

    function updateSignupContext({
      webEntryPointUrl,
      webEntryPointReferrerUrl,
    }) {
      // only update the fields in the signup context if they were not set yet
      signupContext.value.webEntryPointUrl ||= webEntryPointUrl
      signupContext.value.webEntryPointReferrerUrl ||= webEntryPointReferrerUrl
    }

    async function getCorporateEligibilityConfig(corporateKey, lang) {
      if (corporateKey in groupVerificationConfig) {
        const response = await apiRequest.getCorporateEligibilityGroupConfig(
          groupVerificationConfig[corporateKey].groupId,
          lang,
        )
        return response.data.corporate_eligibility_group_config
      }

      const response = await apiRequest.getCorporateEligibilityConfig(
        corporateKey,
        lang,
      )
      return response.data.corporate_eligibility_config
    }

    /**
     * Internal function
     * Takes the verification data from the user input and check it against the
     * eligibility list of one corporate (defined by the corporate_key in the
     * eligibilityData)
     */
    async function checkCorporateOrGroupEligibility(eligibilityData) {
      const groupConfig = groupVerificationConfig[eligibilityData.corporate_key]

      try {
        if (groupConfig !== undefined) {
          eligibilityData.corporate_eligibility_group_id = groupConfig.groupId
          const response =
            await apiRequest.checkCorporateGroupEligibility(eligibilityData)
          const data = {
            ...eligibilityData,
            corporate_key:
              response.data.corporate_key || eligibilityData.corporate_key,
            type: VERIFICATION_METHOD.ELIGIBILITY_LIST,
          }
          corporateEligibilityData.value = data
          return data
        }

        const response =
          await apiRequest.checkCorporateEligibility(eligibilityData)
        const data = {
          ...eligibilityData,
          corporate_key:
            response.data.corporate_key || eligibilityData.corporate_key,
          type: VERIFICATION_METHOD.ELIGIBILITY_LIST,
        }
        corporateEligibilityData.value = data
        return data
      } catch (e) {
        corporateEligibilityData.value = {}
        throw e
      }
    }

    /**
     * Internal function
     * Takes verification data from the user input and checks it against the
     * eligibility lists of the corporates and groups listed in the list param
     */
    async function checkEligibilityForListOfCorporatesAndGroups(
      eligibilityData,
      list,
    ) {
      try {
        return await checkCorporateOrGroupEligibility({
          ...eligibilityData,
          // inject the corporate of the current list to check into the payload
          corporate_key: list[0],
        })
      } catch (e) {
        // if there are other lists to check and no entry in the current list was a
        // match check the next list (recursion) otherwise return the original error
        if (list.length > 1 && e?.response?.data?.error === 'NOT_ELIGIBLE') {
          corporateEligibilityData.value = {}
          return await checkEligibilityForListOfCorporatesAndGroups(
            eligibilityData,
            list.slice(1),
          )
        } else {
          throw e
        }
      }
    }

    /**
     * The only eligibility check function that should be called from the outside
     * Takes verification data from the user input and checks against all relevant
     * eligibility lists (in sequence)
     * Which lists to check is defined in the groupVerificationConfig
     */
    async function checkCorporateEligibility(eligibilityData) {
      // ensure that all checks will have the same eligibility session hash
      eligibilityData.eligibility_session_hash = getOrSetEligibilitySessionHash(
        eligibilityData.corporate_key,
      )

      const groupConfig = groupVerificationConfig[eligibilityData.corporate_key]

      // identify which eligibility lists to check (or rather the lists of which corporates or groups to check)
      const listsToCheck = [
        eligibilityData.corporate_key,
        ...(groupConfig?.otherEligibilityListsToCheck ?? []),
      ]

      return await checkEligibilityForListOfCorporatesAndGroups(
        eligibilityData,
        listsToCheck,
      )
    }

    async function checkEligibilityWithToken(corporate_key, eligible_token) {
      const eligibilitySessionHash =
        getOrSetEligibilitySessionHash(corporate_key)
      const response = await apiRequest.checkEligibilityWithEligibleToken(
        eligibilitySessionHash,
        eligible_token,
      )
      const data = {
        eligibility_session_hash: eligibilitySessionHash,
        eligible_token,
        corporate_key: response.data.corporate_key,
        type: VERIFICATION_METHOD.ELIGIBLE_TOKEN,
      }
      corporateEligibilityData.value = data
      return data
    }

    async function checkVoucher(code) {
      const response = await apiRequest.checkVoucher(code)
      const data = {
        voucher: response.data.corporate_voucher.code,
        corporate_key: response.data.corporate_voucher.corporate_name,
        type: VERIFICATION_METHOD.VOUCHER,
      }
      corporateEligibilityData.value = data
      voucher.value = response.data.corporate_voucher.code
      return data
    }

    return {
      // State
      formData,
      signupContext,
      corporateEligibilityData,
      voucher,
      // Actions
      updateFormData,
      updateSignupContext,
      getCorporateEligibilityConfig,
      checkCorporateEligibility,
      checkEligibilityWithToken,
      checkVoucher,
    }
  },
  {
    persist: {
      storage: sessionStorage,
    },
  },
)

export default useUsStore
